<template>
  <Transition name="fade">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      @click.self="emit('close')"
    >
      <div
        data-testid="new-item-overlay"
        class="relative w-full max-w-[669px] rounded-[20px] border border-tertiary bg-secondary p-8 shadow-2xl"
      >
        <h2 class="mb-6 border-b border-tertiary pb-4 text-2xl font-bold text-text-main">
          Neue Anlegen
        </h2>

        <div class="mb-6 space-y-6 rounded-[20px] border border-tertiary bg-primary p-6">
          <div class="flex gap-6">
            <label class="flex cursor-pointer items-center gap-2">
              <input
                v-model="type"
                data-testid="new-item-type-file"
                type="radio"
                name="fileType"
                value="file"
                class="h-4 w-4 accent-accent"
              />
              <span class="text-sm font-semibold text-text-main"> Neue Datei </span>
            </label>

            <label class="flex cursor-pointer items-center gap-2">
              <input
                v-model="type"
                data-testid="new-item-type-project"
                type="radio"
                name="fileType"
                value="project"
                class="h-4 w-4 accent-accent"
              />
              <span class="text-sm font-semibold text-text-main"> Neues Projekt </span>
            </label>
          </div>

          <div class="flex items-center gap-4">
            <label class="w-12 text-sm font-bold text-text-main"> Name: </label>

            <div class="flex flex-1 items-center border-b border-text-main/30 pb-1">
              <input
                v-model="name"
                data-testid="new-item-name"
                type="text"
                class="flex-1 border-none bg-transparent px-2 font-mono text-sm text-text-main outline-none"
              />

              <span v-if="type === 'file'" class="font-mono text-sm text-text-main/50">
                .java
              </span>
            </div>
          </div>

          <div
            class="flex items-center gap-4 border-t border-tertiary pt-4 text-sm text-text-main/70"
          >
            <span class="font-bold text-text-main">Speicherort:</span>
            <select
              v-if="type === 'file'"
              v-model="selectedProjectId"
              class="flex-1 border-b border-text-main/30 bg-transparent pb-1 font-mono text-sm text-text-main outline-none"
            >
              <option v-if="projects.length === 0" :value="''" disabled>
                Kein Projekt vorhanden
              </option>
              <option v-for="p in projects" :key="p.id" :value="p.id">
                {{ p.name }}
              </option>
            </select>
            <select
              v-else-if="type === 'project' && availableNamespaces.length > 0"
              v-model="selectedNamespaceId"
              class="flex-1 border-b border-text-main/30 bg-transparent pb-1 font-mono text-sm text-text-main outline-none"
            >
              <option v-for="ns in availableNamespaces" :key="ns.id" :value="ns.id">
                {{ ns.name }}
              </option>
            </select>
            <span v-else class="font-mono">Lade...</span>
          </div>
        </div>

        <div class="flex items-center justify-end gap-3">
          <button
            type="button"
            data-testid="new-item-save"
            class="cursor-pointer rounded-standard bg-accent px-8 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-150 hover:brightness-110"
            @click="save"
          >
            {{ saveButtonText }}
          </button>

          <button
            type="button"
            class="cursor-pointer rounded-standard border border-tertiary bg-primary px-6 py-2 text-sm font-semibold text-text-main transition-colors duration-150 hover:bg-primary/80"
            @click="emit('close')"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { API_BASE_URL } from '@/config'

type ItemType = 'file' | 'project'

const props = defineProps<{
  isOpen: boolean
  projects: { id: string; name: string }[]
  defaultProjectId?: string | null
}>()

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'save', data: { type: ItemType; name: string; folderId: string }): void
}>()

const type = ref<ItemType>('file')
const name = ref('')
const selectedProjectId = ref<string>('')
const selectedNamespaceId = ref<string>('')
const availableNamespaces = ref<{ id: string; name: string }[]>([])

// Reset the form each time the dialog opens; pre-select the active project.
watch(
  () => props.isOpen,
  async (open) => {
    if (!open) return
    type.value = 'file'
    name.value = ''
    selectedProjectId.value = props.defaultProjectId ?? props.projects[0]?.id ?? ''

    // Fetch namespaces
    try {
      availableNamespaces.value = []
      const nsRes = await fetch(`${API_BASE_URL}/api/users/namespace`, { credentials: 'include' })
      if (nsRes.ok) {
        const ns = await nsRes.json()
        availableNamespaces.value.push({ id: ns.id, name: 'Eigener Workspace' })
        selectedNamespaceId.value = ns.id
      }

      const groupsRes = await fetch(`${API_BASE_URL}/api/groups/my-groups`, {
        credentials: 'include',
      })
      if (groupsRes.ok) {
        const groups = await groupsRes.json()
        groups.forEach((g: Record<string, unknown>) => {
          if (g.namespace_id) {
            availableNamespaces.value.push({
              id: String(g.namespace_id),
              name: `Gruppe: ${g.name}`,
            })
          }
        })
      }
    } catch (e) {
      console.error(e)
    }
  }
)

const saveButtonText = computed(() => 'Speichern')

function save() {
  const trimmed = name.value.trim()
  if (!trimmed) return

  if (type.value === 'file') {
    if (!selectedProjectId.value) {
      alert('Bitte wähle ein Projekt als Speicherort.')
      return
    }
    // Append .java to match the suffix shown next to the name input.
    const fileName = trimmed.toLowerCase().endsWith('.java') ? trimmed : `${trimmed}.java`
    emit('save', { type: 'file', name: fileName, folderId: selectedProjectId.value })
  } else {
    emit('save', { type: 'project', name: trimmed, folderId: selectedNamespaceId.value })
  }

  emit('close')
  name.value = ''
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
